/**
 * Session Manager Utility for Web
 * Handles session validation, expiration, and cleanup using cookies
 */

import apiService from "../api";

type SessionStatus = {
  isValid: boolean;
  hasValidSession: boolean;
};

class SessionManager {
  private onSessionExpiredCallback: ((message: string) => void) | null = null;

  hasStoredSession(): boolean {
    if (typeof window === "undefined") {
      return false;
    }

    return Boolean(
      localStorage.getItem("accessToken") || localStorage.getItem("refreshToken")
    );
  }

  setSessionExpiredCallback(callback: (message: string) => void): void {
    this.onSessionExpiredCallback = callback;
  }

  clearStoredSession(): void {
    apiService.setTokens(null, null);
  }

  async clearSession(options?: { notifyServer?: boolean; silent?: boolean }): Promise<void> {
    const { notifyServer = true, silent = false } = options || {};
    const hadStoredSession = this.hasStoredSession();

    this.clearStoredSession();

    if (!notifyServer || !hadStoredSession) {
      return;
    }

    try {
      await apiService.logout();
      if (!silent) {
        console.log("Session cleared successfully");
      }
    } catch (error) {
      if (!silent) {
        console.warn("Error clearing session:", error);
      }
    }
  }

  async validateSession(): Promise<SessionStatus> {
    try {
      if (!this.hasStoredSession()) {
        return {
          isValid: false,
          hasValidSession: false,
        };
      }

      const hasValidSession = await apiService.hasValidSession();
      if (!hasValidSession) {
        if (this.onSessionExpiredCallback) {
          this.onSessionExpiredCallback("Your session has expired. Please login again.");
        }
        return {
          isValid: false,
          hasValidSession: false,
        };
      }

      return {
        isValid: true,
        hasValidSession: true,
      };
    } catch (error) {
      console.error("Session validation error:", error);
      return {
        isValid: false,
        hasValidSession: false,
      };
    }
  }

  async isLoggedIn(): Promise<boolean> {
    const status = await this.validateSession();
    return status.isValid;
  }

  triggerSessionExpired(message: string = "Your session has expired. Please login again.") {
    if (this.onSessionExpiredCallback) {
      this.onSessionExpiredCallback(message);
    }
  }
}

const sessionManager = new SessionManager();

export default sessionManager;
